import { useEffect, useCallback } from 'react';
import { useSSE } from '../app/providers/SseProvider';
import { useExecutionsStore } from '../store/executionsStore';
import {
  TRACE_CREATED_SSE_EVENT,
  TRACE_UPDATED_SSE_EVENT,
  type TraceCreatedPayload,
  type TracePreviewDto,
} from '@terra/shared-contracts-trace';
import {
  isTerminalBackendTraceStatus,
  isTerminalNeoAttackStoreStatus,
  mapBackendTraceStatusToNeoAttackStatus,
  resolveNeoAttackTraceSync,
  type NeoAttackStoreStatus,
} from '../utils/neoAttackStatus';
import type { NeoAttackState } from '../store/executionsStore';

type TraceStatus = TracePreviewDto['status'];

type TraceEventType = 'TRACE_CREATED' | 'TRACE_UPDATED';

type ParsedTrace = {
  status: TraceStatus;
  signalId: string | null;
  createdAtMs: number;
};

type ParsedTraceSSEEvent = {
  type: TraceEventType;
  targetId: string;
  threadId: string;
  trace: ParsedTrace | null;
};

function toTraceSSEEvent({
  type,
  data,
}: {
  type: TraceEventType;
  data: TraceCreatedPayload;
}): ParsedTraceSSEEvent {
  const trace = data.trace
    ? {
        status: data.trace.status,
        signalId: data.trace.signalId ?? null,
        createdAtMs: new Date(data.trace.createdAt).getTime(),
      }
    : null;

  return {
    type,
    targetId: data.targetId,
    threadId: data.threadId,
    trace,
  };
}

function ensureRunningNeoInStore(
  event: ParsedTraceSSEEvent,
  getNeoAttackByThreadId: (threadId: string) =>
    | {
        signalId: string;
        state: { threadId: string; executionId: string | null; status: string };
      }
    | undefined,
  addNeoAttack: (params: {
    signalId: string;
    threadId: string;
    executionId: string | null;
    applicationId: string;
  }) => void,
): boolean {
  if (event.trace?.status !== 'RUNNING' || !event.trace.signalId) return false;

  const existing = getNeoAttackByThreadId(event.threadId);

  if (
    existing &&
    isTerminalNeoAttackStoreStatus(
      existing.state.status as NeoAttackStoreStatus,
    )
  ) {
    return false;
  }

  if (
    existing &&
    existing.signalId === event.trace.signalId &&
    existing.state.status === 'running'
  ) {
    return false;
  }

  addNeoAttack({
    signalId: event.trace.signalId,
    threadId: event.threadId,
    executionId: null,
    applicationId: event.targetId,
  });

  return true;
}

function handleTerminalStatus(
  event: ParsedTraceSSEEvent,
  getNeoAttackByThreadId: (threadId: string) =>
    | {
        signalId: string;
        state: { threadId: string; executionId: string | null; status: string };
      }
    | undefined,
  getNeoAttackState: (signalId: string) => NeoAttackState | undefined,
  updateNeoAttackStatus: (params: {
    signalId: string;
    status: 'completed' | 'failed';
    threadId?: string;
  }) => void,
  onNeoAttackComplete:
    | ((params: {
        signalId: string;
        threadId: string;
        success: boolean;
      }) => void)
    | undefined,
): boolean {
  const status = event.trace?.status;

  if (!status || !isTerminalBackendTraceStatus(status)) return false;

  const neoAttack = getNeoAttackByThreadId(event.threadId);

  if (neoAttack) {
    const mappedStatus = mapBackendTraceStatusToNeoAttackStatus(status);

    if (mappedStatus !== 'completed' && mappedStatus !== 'failed') {
      return false;
    }

    if (neoAttack.state.status === mappedStatus) {
      return false;
    }

    updateNeoAttackStatus({
      signalId: neoAttack.signalId,
      status: mappedStatus,
    });
    onNeoAttackComplete?.({
      signalId: neoAttack.signalId,
      threadId: event.threadId,
      success: mappedStatus === 'completed',
    });

    return true;
  }

  const signalId = event.trace?.signalId;

  if (!signalId || !event.trace) return false;

  const queuedAttack = getNeoAttackState(signalId);

  if (!queuedAttack) return false;

  const sync = resolveNeoAttackTraceSync({
    traceStatus: status,
    traceThreadId: event.threadId,
    traceCreatedAtMs: event.trace.createdAtMs,
    current: {
      status: queuedAttack.status,
      threadId: queuedAttack.threadId,
      startedAtMs: new Date(queuedAttack.startedAt).getTime(),
    },
  });

  if (sync.kind !== 'adopt-terminal') return false;

  updateNeoAttackStatus({
    signalId,
    status: sync.status,
    threadId: event.threadId,
  });
  onNeoAttackComplete?.({
    signalId,
    threadId: event.threadId,
    success: sync.status === 'completed',
  });

  return true;
}

type UseNeoAttackSSEParams = {
  applicationId?: string;
  enabled?: boolean;
  onTraceEvent?: () => void;
  onNeoAttackComplete?: (params: {
    signalId: string;
    threadId: string;
    success: boolean;
  }) => void;
};

export function useNeoAttackSSE({
  applicationId,
  enabled = true,
  onTraceEvent,
  onNeoAttackComplete,
}: UseNeoAttackSSEParams) {
  const { subscribe } = useSSE();
  const {
    getNeoAttackByThreadId,
    getNeoAttackState,
    updateNeoAttackStatus,
    addNeoAttack,
  } = useExecutionsStore();

  const handleTraceEvent = useCallback(
    (event: ParsedTraceSSEEvent) => {
      if (event.targetId !== applicationId) return;

      const startedOrRunningChanged = ensureRunningNeoInStore(
        event,
        getNeoAttackByThreadId,
        addNeoAttack,
      );
      const terminalStatusChanged = handleTerminalStatus(
        event,
        getNeoAttackByThreadId,
        getNeoAttackState,
        updateNeoAttackStatus,
        onNeoAttackComplete,
      );

      if (startedOrRunningChanged || terminalStatusChanged) {
        onTraceEvent?.();
      }
    },
    [
      applicationId,
      addNeoAttack,
      getNeoAttackByThreadId,
      getNeoAttackState,
      onTraceEvent,
      updateNeoAttackStatus,
      onNeoAttackComplete,
    ],
  );

  useEffect(() => {
    if (!enabled || !applicationId) return;

    const unsubscribers = [
      subscribe(TRACE_CREATED_SSE_EVENT, (data) => {
        handleTraceEvent(toTraceSSEEvent({ type: 'TRACE_CREATED', data }));
      }),
      subscribe(TRACE_UPDATED_SSE_EVENT, (data) => {
        handleTraceEvent(toTraceSSEEvent({ type: 'TRACE_UPDATED', data }));
      }),
    ];

    return () => {
      for (const unsubscribe of unsubscribers) unsubscribe();
    };
  }, [subscribe, handleTraceEvent, enabled, applicationId]);
}
